import type { CpotIndicator, Recommendation, RecommendationCriteria, VisibleRecommendation, WorkflowRoute, WorkflowRouteId } from "./types";
import type { WorkflowState } from "../lib/state";

export const workflowRoutes: WorkflowRoute[] = [
  {
    id: "start",
    path: "/start",
    label: "Start",
    shortLabel: "Start",
    group: "entry",
    summary: "",
  },
  {
    id: "setup",
    path: "/setup",
    label: "Patient details",
    shortLabel: "Details",
    group: "entry",
    summary: "",
  },
  {
    id: "tools",
    path: "/tools",
    label: "Tools",
    shortLabel: "Tools",
    group: "tool",
    summary: "NPRS, functional assessment, CPOT, and RASS.",
  },
  {
    id: "itu-sedated",
    path: "/itu-intubated",
    label: "Intubated phase",
    shortLabel: "Intubated",
    group: "workflow",
    summary: "",
  },
  {
    id: "after-extubation",
    path: "/after-extubation",
    label: "Extubated phase",
    shortLabel: "Extubated",
    group: "workflow",
    summary: "",
  },
  {
    id: "breakthrough-pain",
    path: "/breakthrough-pain",
    label: "Breakthrough pain",
    shortLabel: "Breakthrough",
    group: "workflow",
    summary: "",
  },
  {
    id: "pain-score",
    path: "/tools/pain-score",
    label: "NPRS tool",
    shortLabel: "NPRS",
    group: "tool",
    summary: "Numerical Pain Rating Scale.",
  },
  {
    id: "function",
    path: "/tools/function",
    label: "Function tool",
    shortLabel: "Function",
    group: "tool",
    summary: "Functional limitation assessment for breathing, coughing, mobility, and transfers.",
  },
  {
    id: "cpot",
    path: "/tools/cpot",
    label: "CPOT tool",
    shortLabel: "CPOT",
    group: "tool",
    summary: "Critical-Care Pain Observation Tool.",
  },
  {
    id: "rass",
    path: "/tools/rass",
    label: "RASS tool",
    shortLabel: "RASS",
    group: "tool",
    summary: "Richmond Agitation-Sedation Scale.",
  },
];

export const routeByPath = new Map(workflowRoutes.map((route) => [route.path, route]));
export const routeById = new Map(workflowRoutes.map((route) => [route.id, route]));

export const flagLabels: Record<string, string> = {
  painRiskFactors: "patient at risk of challenging pain management",
  chronicGabapentinoid: "chronic gabapentinoid use",
  polyneuropathy: "central/peripheral polyneuropathy",
  persistentPostOpPainRisk: "persistent post-operative pain risk",
  neuropathicPain: "neuropathic features",
  oralNotTolerated: "oral route not tolerated",
  functionalLimitation: "significant functional limitation",
  nonStandardPostopPathway: "patient does not follow the standard cardiac postoperative pathway",
  bmiAbove30: "BMI above 30",
  weightAbove50: "patient weight above 50 kg",
  ischaemicHeartDisease: "ischaemic heart disease",
  congestiveHeartFailure: "congestive heart failure",
  cerebrovascularDisease: "cerebrovascular disease",
  peripheralVascularDisease: "peripheral vascular disease",
  renalImpairment: "renal impairment",
  nsaidContraindication: "any NSAID contraindication",
  giBleedingHistory: "history of GI bleeding",
  coagulationDefect: "coagulation defect",
  uncontrolledHypertension: "uncontrolled hypertension",
  dexmedetomidineAvoidance: "any dexmedetomidine contraindication",
  highDegreeHeartBlock: "high-degree heart block",
  severeBradycardia: "severe bradycardia",
  hypotensionNoradrenaline: "hypotension requiring noradrenaline above 0.1 mcg/kg/min",
  paracetamolAllergy: "paracetamol allergy",
  pregabalinAllergy: "pregabalin allergy or intolerance",
  gabapentinAllergy: "gabapentin allergy or intolerance",
  morphineAllergy: "morphine allergy",
  oxycodoneAllergy: "oxycodone allergy",
  fentanylAllergy: "fentanyl allergy",
  alfentanilAllergy: "alfentanil allergy",
  remifentanilAllergy: "remifentanil allergy",
  ketamineAllergy: "ketamine allergy",
  dexmedetomidineAllergy: "dexmedetomidine allergy",
  magnesiumAllergy: "magnesium sulphate allergy",
  nsaidAllergy: "ibuprofen/NSAID allergy",
  buprenorphineAllergy: "buprenorphine allergy",
  dihydrocodeineAllergy: "dihydrocodeine allergy",
};

export const recommendations: Recommendation[] = [
  {
    id: "sedated-targets",
    routeIds: ["itu-sedated"],
    cardId: "sedated-targets",
    cardTitle: "Pain and sedation targets",
    cardKind: "target",
    title: "Immediate post-operative pain and sedation targets",
    strength: "recommended",
    summary: "RASS: 0 to +1.",
    details: ["CPOT: 2 or less.", "NPRS: Below 4 (mild pain).", "Document pain scores hourly.", "Extubate when appropriate."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-propofol",
    routeIds: ["itu-sedated"],
    cardId: "sedated-propofol",
    cardTitle: "Propofol IV infusion",
    cardKind: "intervention",
    title: "Post-operative sedation",
    strength: "recommended",
    summary: "1 mg per kg per hour infusion.",
    details: ["Stop propofol infusion based on the ERACS pathway in preparation for extubation."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-dexmedetomidine",
    routeIds: ["itu-sedated"],
    cardId: "sedated-dexmedetomidine",
    cardTitle: "Dexmedetomidine IV infusion",
    cardKind: "intervention",
    blockedByFlags: ["dexmedetomidineAllergy"],
    contraindicatedByFlags: ["dexmedetomidineAvoidance"],
    title: "Dexmedetomidine",
    strength: "recommended",
    summary: "Continue infusion at 0.1 to 0.4 mcg per kg per hour for a maximum of four hours after extubation.",
    details: [
      "If the patient is not waking within one hour after stopping propofol, reduce dexmedetomidine by 50%. Stop dexmedetomidine one hour after the first oral analgesia has been given.",
    ],
    cautions: [
      "Stop dexmedetomidine infusion if any of: RASS less than -1, respiratory acidosis (pH less than 7.3 with pCO2 more than 6.0 kPa), systolic BP less than 90 mmHg, heart rate less than 50.",
    ],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-dexmedetomidine-avoid",
    routeIds: ["itu-sedated"],
    cardId: "sedated-dexmedetomidine",
    cardTitle: "Dexmedetomidine",
    cardKind: "intervention",
    title: "Dexmedetomidine avoidance",
    strength: "caution",
    summary: "Avoid dexmedetomidine when high-degree heart block, severe bradycardia, hypotension requiring noradrenaline above 0.1 mcg/kg/min, RASS below -1, respiratory rate below 10, respiratory acidosis, systolic BP below 90 mmHg, or HR below 50 is present.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { flagsAny: ["dexmedetomidineAvoidance"] },
  },
  {
    id: "sedated-breakthrough-morphine",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-morphine",
    cardTitle: "Morphine IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["morphineAllergy"],
    title: "Breakthrough pain: morphine",
    strength: "recommended",
    summary: "2 mg bolus every 2 to 5 minutes (up to 20 mg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["morphine"] },
  },
  {
    id: "sedated-breakthrough-oxycodone",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-oxycodone",
    cardTitle: "Oxycodone IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["oxycodoneAllergy"],
    title: "Breakthrough pain: oxycodone",
    strength: "recommended",
    summary: "2 mg bolus every 2 to 5 minutes (up to 20 mg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["oxycodone"] },
  },
  {
    id: "sedated-breakthrough-fentanyl",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-fentanyl",
    cardTitle: "Fentanyl IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["fentanylAllergy"],
    title: "Breakthrough pain: fentanyl",
    strength: "recommended",
    summary: "20 mcg bolus every 2 to 5 minutes (up to 200 mcg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["fentanyl"] },
  },
  {
    id: "extubated-targets",
    routeIds: ["after-extubation"],
    cardId: "extubated-targets",
    cardTitle: "Pain and sedation targets",
    cardKind: "target",
    title: "After extubation targets",
    strength: "recommended",
    summary: "RASS: 0 to +2.",
    details: ["CPOT: 2 or less.", "NPRS: below 4 (mild pain)."],
    sourceRefs: [{ page: 6, section: "After extubation" }],
  },
  {
    id: "oral-dihydrocodeine",
    routeIds: ["after-extubation"],
    cardId: "oral-dihydrocodeine",
    cardTitle: "Dihydrocodeine oral",
    cardKind: "intervention",
    title: "Dihydrocodeine",
    strength: "recommended",
    blockedByFlags: ["dihydrocodeineAllergy"],
    summary: "30 to 60 mg four times per day.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "oral-morphine",
    routeIds: ["after-extubation"],
    cardId: "oral-morphine",
    cardTitle: "Morphine IR oral",
    cardKind: "intervention",
    title: "Morphine",
    strength: "recommended",
    blockedByFlags: ["morphineAllergy"],
    summary: "10 to 20 mg every two hours (PRN).",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "oral-oxycodone",
    routeIds: ["after-extubation"],
    cardId: "oral-oxycodone",
    cardTitle: "Oxycodone IR oral",
    cardKind: "intervention",
    title: "Oxycodone",
    strength: "recommended",
    blockedByFlags: ["oxycodoneAllergy"],
    summary: "2.5 mg four times per day (PRN).",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "oral-buprenorphine",
    routeIds: ["after-extubation"],
    cardId: "oral-buprenorphine",
    cardTitle: "Buprenorphine sublingual",
    cardKind: "intervention",
    title: "Buprenorphine",
    strength: "recommended",
    blockedByFlags: ["buprenorphineAllergy"],
    summary: "200 mcg four times per day.",
    details: ["200 mcg every four hours (PRN).", "Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["lt20"] },
  },
  {
    id: "oral-paracetamol-over-50kg",
    routeIds: ["after-extubation"],
    cardId: "oral-paracetamol",
    cardTitle: "Paracetamol oral",
    cardKind: "intervention",
    blockedByFlags: ["paracetamolAllergy"],
    title: "Regular oral paracetamol",
    strength: "recommended",
    summary: "1 g four times per day.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { flagsAny: ["weightAbove50"] },
  },
  {
    id: "nsaid-caution",
    routeIds: ["after-extubation"],
    cardId: "nsaid-caution",
    cardTitle: "Ibuprofen oral",
    cardKind: "intervention",
    blockedByFlags: ["nsaidAllergy"],
    title: "Ibuprofen",
    strength: "caution",
    summary: "400 mg three times per day, maximum of 72 hours.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    cautions: ["Requires ACCU consultant or senior physician review before use."],
    sourceRefs: [{ page: 6, section: "NSAIDs" }],
  },
  {
    id: "nsaid-contraindication",
    routeIds: ["after-extubation"],
    cardId: "nsaid-caution",
    cardTitle: "Ibuprofen oral",
    cardKind: "intervention",
    blockedByFlags: ["nsaidAllergy"],
    title: "Ibuprofen contraindication present",
    strength: "caution",
    summary: "Avoid ibuprofen when a listed contraindication is present.",
    details: [],
    sourceRefs: [{ page: 6, section: "NSAIDs" }],
    criteria: {
      flagsAny: ["nsaidContraindication"],
    },
  },
  {
    id: "pca-morphine",
    routeIds: ["after-extubation"],
    cardId: "pca-morphine",
    cardTitle: "Morphine IV PCA",
    cardKind: "intervention",
    blockedByFlags: ["morphineAllergy"],
    title: "PCA: morphine",
    strength: "escalate",
    summary: "",
    details: ["Use only when regular oral analgesia and maximum top-up IV boluses have been given, or when the oral route is not tolerated."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001)."],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["morphine"], painAtLeast: 5 },
        { opioid: ["morphine"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "extubated-breakthrough-morphine",
    routeIds: ["after-extubation"],
    cardId: "extubated-breakthrough-morphine",
    cardTitle: "Morphine IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["morphineAllergy"],
    title: "Breakthrough pain: morphine",
    strength: "escalate",
    summary: "2 mg bolus every 2 to 5 minutes (up to 20 mg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }, { page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["morphine"], painAtLeast: 5 },
        { opioid: ["morphine"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "pca-oxycodone",
    routeIds: ["after-extubation"],
    cardId: "pca-oxycodone",
    cardTitle: "Oxycodone IV PCA",
    cardKind: "intervention",
    blockedByFlags: ["oxycodoneAllergy"],
    title: "PCA: oxycodone",
    strength: "escalate",
    summary: "",
    details: ["Use only when regular oral analgesia and maximum top-up IV boluses have been given, or when the oral route is not tolerated."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001)."],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["oxycodone"], painAtLeast: 5 },
        { opioid: ["oxycodone"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "extubated-breakthrough-oxycodone",
    routeIds: ["after-extubation"],
    cardId: "extubated-breakthrough-oxycodone",
    cardTitle: "Oxycodone IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["oxycodoneAllergy"],
    title: "Breakthrough pain: oxycodone",
    strength: "escalate",
    summary: "2 mg bolus every 2 to 5 minutes (up to 20 mg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }, { page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["oxycodone"], painAtLeast: 5 },
        { opioid: ["oxycodone"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "pca-fentanyl",
    routeIds: ["after-extubation"],
    cardId: "pca-fentanyl",
    cardTitle: "Fentanyl IV PCA",
    cardKind: "intervention",
    blockedByFlags: ["fentanylAllergy"],
    title: "PCA: fentanyl",
    strength: "escalate",
    summary: "",
    details: ["Use only when regular oral analgesia and maximum top-up IV boluses have been given, or when the oral route is not tolerated."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001)."],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["fentanyl"], painAtLeast: 5 },
        { opioid: ["fentanyl"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "extubated-breakthrough-fentanyl",
    routeIds: ["after-extubation"],
    cardId: "extubated-breakthrough-fentanyl",
    cardTitle: "Fentanyl IV bolus",
    cardKind: "intervention",
    blockedByFlags: ["fentanylAllergy"],
    title: "Breakthrough pain: fentanyl",
    strength: "escalate",
    summary: "20 mcg bolus every 2 to 5 minutes (up to 200 mcg).",
    details: ["Use the same opioid as administered intra-operatively until pain control is achieved."],
    cautions: ["See Patient and Nurse Controlled Analgesia Guideline (CLI/GUID/149/2019-001) and use the Intravenous Opioid Care Plan."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }, { page: 6, section: "PCA escalation" }],
    criteria: {
      any: [
        { opioid: ["fentanyl"], painAtLeast: 5 },
        { opioid: ["fentanyl"], flagsAny: ["oralNotTolerated", "functionalLimitation"] },
      ],
    },
  },
  {
    id: "postop-ketamine",
    routeIds: ["after-extubation"],
    cardId: "postop-ketamine",
    cardTitle: "Ketamine",
    cardKind: "intervention",
    blockedByFlags: ["ketamineAllergy"],
    title: "Ketamine escalation",
    strength: "escalate",
    summary: "0.1 to 0.3 mg per kg per hour IV infusion for 24 to 48 hours in Level 2 or 3 wards, transitioning to 50 mg oral four times per day, with first dose one hour after stopping the infusion, tapering over 48 to 72 hours.",
    details: [],
    cautions: ["Discuss with the ITU consultant before starting ketamine for challenging pain with significant functional limitation and refer to the Acute Pain Management Team for follow-up."],
    sourceRefs: [{ page: 6, section: "Ketamine" }, { page: 7, section: "Ketamine continued" }],
    criteria: { flagsAny: ["painRiskFactors", "functionalLimitation"] },
  },
  {
    id: "postop-gabapentinoids",
    routeIds: ["after-extubation"],
    cardId: "postop-gabapentinoids",
    cardTitle: "Pregabalin oral",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Gabapentinoids for neuropathic features",
    strength: "escalate",
    summary: "75 mg twice per day for 4 to 6 weeks.",
    details: [],
    cautions: ["Discuss with Acute Pain Management Team before starting post-operative gabapentinoids. Common side-effects include drowsiness, dizziness, confusion, and malaise. If present, omit morning dose and consider reducing evening dose."],
    sourceRefs: [{ page: 7, section: "Gabapentinoids" }],
    criteria: { egfr: ["gt40"], flagsAny: ["neuropathicPain"] },
  },
  {
    id: "postop-gabapentinoids-30to40",
    routeIds: ["after-extubation"],
    cardId: "postop-gabapentinoids",
    cardTitle: "Pregabalin oral",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Gabapentinoids for neuropathic features",
    strength: "escalate",
    summary: "50 mg twice per day for 4 to 6 weeks.",
    details: [],
    cautions: ["Discuss with Acute Pain Management Team before starting post-operative gabapentinoids. Common side-effects include drowsiness, dizziness, confusion, and malaise. If present, omit morning dose and consider reducing evening dose."],
    sourceRefs: [{ page: 7, section: "Gabapentinoids" }],
    criteria: { egfr: ["30to40"], flagsAny: ["neuropathicPain"] },
  },
  {
    id: "non-pharm",
    routeIds: ["after-extubation"],
    cardId: "non-pharm",
    cardTitle: "Non-pharmacological approaches",
    cardKind: "intervention",
    title: "Non-pharmacological adjuncts",
    strength: "recommended",
    summary: "Music, massage and other non-pharmacological approaches are recommended as an adjunct to pharmacological therapy.",
    details: [],
    sourceRefs: [{ page: 7, section: "Non-pharmacological approaches" }],
  },
];

export const painScaleRows = [
  { range: "0", label: "No pain" },
  { range: "1-3", label: "Mild pain" },
  { range: "4-6", label: "Moderate pain" },
  { range: "7-10", label: "Severe pain" },
];

export const functionalAssessment = [
  {
    grade: "A",
    label: "No limitation",
    description: "The patient can undertake the activity without limitation due to pain, with pain intensity typically 0-3.",
  },
  {
    grade: "B",
    label: "Mild limitation",
    description: "The patient can undertake the activity but experiences moderate to severe pain, with pain intensity typically 4-10.",
  },
  {
    grade: "C",
    label: "Significant limitation",
    description: "The patient is unable to complete the activity due to pain or pain-treatment adverse effects, independent of pain intensity score.",
  },
];

export const rassRows = [
  { score: "+4", term: "Combative", description: "Overtly combative or violent; immediate danger to staff." },
  { score: "+3", term: "Very agitated", description: "Pulls or removes tubes or catheters, or has aggressive behaviour toward staff." },
  { score: "+2", term: "Agitated", description: "Frequent non-purposeful movement or patient-ventilator dyssynchrony." },
  { score: "+1", term: "Restless", description: "Anxious or apprehensive but movements are not aggressive or vigorous." },
  { score: "0", term: "Alert and calm", description: "Spontaneously pays attention to caregiver." },
  { score: "-1", term: "Drowsy", description: "Not fully alert, but has sustained awakening with eye contact to voice for at least 10 seconds." },
  { score: "-2", term: "Light sedation", description: "Briefly awakens with eye contact to voice for less than 10 seconds." },
  { score: "-3", term: "Moderate sedation", description: "Movement or eye opening to voice, but no eye contact." },
  { score: "-4", term: "Deep sedation", description: "No response to voice, but movement or eye opening to physical stimulation." },
  { score: "-5", term: "Unarousable", description: "No response to voice or physical stimulation." },
];

export const cpotIndicators: CpotIndicator[] = [
  {
    id: "facial",
    label: "Facial expression",
    patientGroup: "all",
    options: [
      { label: "Relaxed, neutral", score: 0, description: "No muscle tension observed." },
      { label: "Tense", score: 1, description: "Frowning, brow lowering, orbit tightening, levator contraction, or eye opening/tearing during nociception." },
      { label: "Grimacing", score: 2, description: "Clear grimacing expression." },
    ],
  },
  {
    id: "movement",
    label: "Body movements",
    patientGroup: "all",
    options: [
      { label: "Absence of movements or normal position", score: 0, description: "No movement or normal movement not aimed toward the pain site." },
      { label: "Protection", score: 1, description: "Slow cautious movements, touching or rubbing pain site, or seeking attention through movements." },
      { label: "Restlessness / agitation", score: 2, description: "Pulling tube, attempting to sit up, thrashing, not following commands, striking staff, or trying to climb out of bed." },
    ],
  },
  {
    id: "ventilator",
    label: "Compliance with ventilator",
    patientGroup: "intubated",
    options: [
      { label: "Tolerating ventilator or movement", score: 0, description: "Alarms not activated, easy ventilation." },
      { label: "Coughing but tolerating", score: 1, description: "Coughing; alarms may activate but stop spontaneously." },
      { label: "Fighting ventilator", score: 2, description: "Asynchrony, blocking ventilation, alarms frequently activated." },
    ],
  },
  {
    id: "vocalisation",
    label: "Vocalisation",
    patientGroup: "extubated",
    options: [
      { label: "Normal tone or no sound", score: 0, description: "Talking in a normal tone or no sound." },
      { label: "Sighing, moaning", score: 1, description: "Audible sighing or moaning." },
      { label: "Crying out, sobbing", score: 2, description: "Crying out or sobbing." },
    ],
  },
  {
    id: "muscle",
    label: "Muscle tension",
    patientGroup: "all",
    options: [
      { label: "Relaxed", score: 0, description: "No resistance to passive movements." },
      { label: "Tense, rigid", score: 1, description: "Resistance to passive movements." },
      { label: "Very tense or rigid", score: 2, description: "Strong resistance to passive movements or incapacity to complete them." },
    ],
  },
];

export function criteriaMatches(
  criteria: RecommendationCriteria | undefined,
  state: WorkflowState,
): { isMatch: boolean; missingContext: string[]; hasHardMismatch: boolean } {
  const missingContext: string[] = [];
  if (!criteria) {
    return { isMatch: true, missingContext, hasHardMismatch: false };
  }

  if (criteria.any && criteria.any.length > 0) {
    const branchResults = criteria.any.map((branch) => criteriaMatches(branch, state));
    if (branchResults.some((result) => result.isMatch)) {
      return { isMatch: true, missingContext, hasHardMismatch: false };
    }

    const possibleBranches = branchResults.filter((result) => !result.hasHardMismatch);
    if (possibleBranches.length > 0) {
      return {
        isMatch: false,
        missingContext: Array.from(new Set(possibleBranches.flatMap((result) => result.missingContext))),
        hasHardMismatch: false,
      };
    }

    return { isMatch: false, missingContext, hasHardMismatch: true };
  }

  let hasHardMismatch = false;
  if (criteria.age) {
    if (state.age === "unknown") {
      missingContext.push("age band");
    } else if (!criteria.age.includes(state.age)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.egfr) {
    if (state.egfr === "unknown") {
      missingContext.push("eGFR band");
    } else if (!criteria.egfr.includes(state.egfr)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.sedation) {
    if (state.sedation === "unknown") {
      missingContext.push("sedation state");
    } else if (!criteria.sedation.includes(state.sedation)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.opioid) {
    if (state.opioid === "unknown") {
      missingContext.push("intra-operative opioid");
    } else if (!criteria.opioid.includes(state.opioid)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.flagsAny && criteria.flagsAny.length > 0) {
    const hasFlag = criteria.flagsAny.some((flag) => state.flags.includes(flag as never));
    if (!hasFlag) {
      const allAnsweredNo = criteria.flagsAny.every((flag) => state.noFlags.includes(flag as never));
      if (allAnsweredNo) {
        hasHardMismatch = true;
      } else {
        missingContext.push(`one of: ${formatClinicalList(criteria.flagsAny.map((flag) => flagLabels[flag] ?? flag))}`);
      }
    }
  }

  if (criteria.painAtLeast !== undefined) {
    if (state.pain === null) {
      missingContext.push("pain score");
    } else if (state.pain < criteria.painAtLeast) {
      hasHardMismatch = true;
    }
  }

  if (criteria.cpotAtLeast !== undefined) {
    if (state.cpot === null) {
      missingContext.push("CPOT");
    } else if (state.cpot < criteria.cpotAtLeast) {
      hasHardMismatch = true;
    }
  }

  return { isMatch: !hasHardMismatch && missingContext.length === 0, missingContext, hasHardMismatch };
}

function formatClinicalList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "the required patient detail";
  if (values.length === 2) return `${values[0]} or ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, or ${values[values.length - 1]}`;
}

export function visibleRecommendations(routeId: WorkflowRouteId, state: WorkflowState): VisibleRecommendation[] {
  return recommendations
    .filter((recommendation) => recommendation.routeIds.includes(routeId))
    .map((recommendation) => {
      const result = criteriaMatches(recommendation.criteria, state);
      return {
        ...recommendation,
        isContextualMatch: result.isMatch,
        missingContext: result.missingContext,
        hasHardMismatch: result.hasHardMismatch,
      };
    })
    .filter((recommendation) => recommendation.isContextualMatch || (recommendation.missingContext.length > 0 && !recommendation.hasHardMismatch));
}
